<script setup lang="ts">
import "@cisco-u/link/cu-link.js";
import "@cisco-u/reicon-lock/cu-reicon-lock.js";

import dayjs from "dayjs";
import { debounce, isEmpty } from "lodash-es";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import AnchorTargetBlankCallback from "@/components/AnchorTargetBlankCallback.vue";
import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { addUserHistory } from "@/services/profileService/addUserHistory";
import isUnauth from "@/utils/isUnauth";
import { toggleMyListItem } from "@/utils/myListActions";
import telemetry from "@/utils/telemetry";

const videoTrack: string = "video-track";

interface Props {
  result: any;
  index?: number;
  status?: any;
  forCarousel?: boolean;
  cardView?: string;
  cardStyle?: string;
  priceView?: string;
  topicView?: boolean;
  isStoreContent?: boolean;
  showDescription?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  status: null,
  forCarousel: false,
  cardView: "Grid",
  cardStyle: "Grid",
  priceView: "USD",
  topicView: false,
  isStoreContent: false,
  showDescription: true,
});

const route = useRoute();
const router = useRouter();
const store = useStore();

// Data
const click = ref(false);

// Computed
const subLink = computed(() => {
  return router.resolve({
    name: "subscription",
  }).href;
});

const inMyList = computed(() => store.getters["user/inMyList"]);
const contentAuthorized = computed(
  () => store.getters["content/authorizedContent"]
);

const isVideoSeries = computed(() => {
  if (props.result.type == undefined) {
    return props.result.hasOwnProperty("track_data");
  }
  return ["videoSeries", videoTrack, "track"].includes(props.result.type);
});

const videoType = computed(() => {
  if (isVideoSeries.value) {
    return "track";
  }
  return "video";
});

const TrackFriendlyUrl = computed(() => {
  if (props.result.hasOwnProperty("tracks")) {
    const tracks = props.result.tracks;
    if (tracks) {
      return tracks[0]?.friendly_url ? tracks[0]?.friendly_url : tracks[0]?.id;
    }
    return null;
  }
  if (isVideoSeries.value) {
    return isEmpty(props.result.friendly_url)
      ? props.result.id
      : props.result.friendly_url;
  }
  const tfUrl = props.result?.track_friendly_urls;

  if (tfUrl?.length > 0) {
    return tfUrl[0];
  }
  return props.result?.track_ids ? props.result?.track_ids[0] : null;
});

const VideoUrl = computed((): string => {
  if (isVideoSeries.value) {
    return "";
  }
  return props.result.friendly_url
    ? props.result.friendly_url
    : props.result.id;
});

const showPremium = computed((): boolean => {
  if (globalThis.env.ENABLE_LOCK_CONTENT) {
    return Boolean(props.result?.lock_content);
  }
  return !props.status?.userEntitlements?.includes(props.result.guid);
});

const resultLink = computed(() => {
  const videoSerieId = TrackFriendlyUrl.value;
  const videoId = VideoUrl.value;
  if (
    route.name == "search-type" &&
    route.params?.type == "video" &&
    (!videoSerieId || !videoId)
  ) {
    return "";
  }

  if (!videoSerieId) {
    return "";
  }
  return router.resolve({
    name: "VideoSeries",
    params: { videoSerieId: videoSerieId, videoId: videoId },
    query: { return_url: encodeURIComponent(globalThis.location.href) },
  }).href;
});

// Methods
const hideClicked = () => {
  if (click.value && showPremium.value) {
    click.value = false;
  }
};

const bookmarkMe = (content: any) => {
  // For videos, we need to use the computed videoType and modify the content
  const contentWithVideoType = { ...content, type: videoType.value };
  toggleMyListItem(contentWithVideoType, videoType.value, {
    inMyList: (item) => inMyList.value(item),
    addContentToMyList: (payload: any) =>
      store.dispatch("user/addContentToMyList", payload),
    removeContentFromMyList: (payload: any) =>
      store.dispatch("user/removeContentFromMyList", payload),
    route: route,
  });
};

const debouncedBookmarkMe = debounce(
  (content: any) => bookmarkMe(content),
  500
);

const whereTo = () => {
  if (showPremium.value) {
    click.value = true;
  } else {
    click.value = false;
    sendTelemetry();
  }
};

const sendTelemetry = async () => {
  const telemetryResponse = telemetry.external(
    {
      action: "launch-contentType",
      url: click.value ? subLink.value : props.result.id,
      title: click.value ? "Subscribe to Cisco U." : props.result.title,
      id: props.result.id,
      page: route.fullPath,
      type: click.value ? "subscription offer" : props.result.type,
      relevance: props.result.relevance,
      sortOrder: props.index,
    },
    isUnauth()
  );
  if (!isUnauth()) {
    await addUserHistory({
      content_id: props.result.id,
      type: videoType.value,
      accessed: dayjs().toISOString(),
    });
  }
  telemetryResponse
    .then(() => {
      if (showPremium.value && subLink.value) {
        globalThis.open(subLink.value, "_blank", "noopener");
      } else {
        router.push(resultLink.value);
      }
    })
    .catch(() => {
      // do nothing
    });
};

// Expose properties and methods for testing
defineExpose({
  showPremium,
  hideClicked,
  isVideoSeries,
  addContentToMyList: (payload: any) =>
    store.dispatch("user/addContentToMyList", payload),
  removeContentFromMyList: (payload: any) =>
    store.dispatch("user/removeContentFromMyList", payload),
  bookmarkMe,
  debouncedBookmarkMe,
  TrackFriendlyUrl,
  resultLink,
});
</script>

<template>
  <div
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
    data-cy="videos-card"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :link="resultLink"
      :cy="'video-card-link'"
      :show-description="props.showDescription"
      @navigate="whereTo"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :link="resultLink"
      @navigate="whereTo"
      :cy="'video-product-card-link'"
      :show-description="props.showDescription"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <div
      :class="[
        'mt-0',
        {
          hidden: !showPremium || !click,
          'mask_it absolute left-0 top-0 mt-0 h-full w-full rounded-[0.625rem] bg-[#000000]/80':
            showPremium && click,
        },
      ]"
      v-if="click"
    >
      <cu-reicon-lock
        type="filled-keyhole"
        dark="true"
        size="xl"
        class="mt-4 flex justify-center"
      />
      <div
        class="mx-12 text-center text-13 text-white-lightest"
        v-click-outside="{
          handler: hideClicked,
          events: ['dblclick', 'click'],
          capture: true,
        }"
      >
        <div v-if="isUnauth() && contentAuthorized(result.guid)">
          <cu-link
            href="/login"
            color="white-lightest"
            nounderline="true"
            class="text-white-lightest underline"
            >Sign up</cu-link
          >
        </div>
        <AnchorTargetBlankCallback
          v-else
          :url="subLink"
          class="underline"
          @clicked="sendTelemetry"
        >
          <div>Upgrade your subscription</div>
        </AnchorTargetBlankCallback>
        <div v-if="isUnauth() && contentAuthorized(result.guid)">
          to explore this free video.
        </div>
        <div v-else>to unlock this video.</div>
      </div>
    </div>
  </div>
</template>
